import { css } from '@emotion/react';
import { Box, Stack, styled } from '@mui/material';
import { createFileRoute, Outlet, stripSearchParams } from '@tanstack/react-router';
import { MapProvider } from '@vis.gl/react-maplibre';
import { z } from 'zod';

import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { HeaderWrapper } from '@/components/HeaderWrapper';
import { PublicMap } from '@/components/PublicMap';
import { useOptionalUserQuery } from '@/hooks/useOptionalUserQuery';
import { useScreenSize } from '@/hooks/useScreenSize';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { zFeatureType } from '@/types/FeatureType';
import { zMapLayer } from '@/types/MapLayer';

const Root = styled(Stack)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100svh;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      width: 100vw;
      height: 100vh;
    }
  `,
);

const MapWrapper = styled(Box)(
  ({ theme }) => css`
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      flex-direction: row;
    }
  `,
);

const FooterWrapper = styled(Box)(
  ({ theme }) => css`
    display: none;
    bottom: 0;
    width: 100%;
    background-color: ${theme.palette.common.white};

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      display: flex;
    }
  `,
);

const defaultSearchValues = {
  layers: zMapLayer.options,
};

const zMapSearchBase = z.object({
  layers: z.array(zMapLayer).default(defaultSearchValues.layers).catch(defaultSearchValues.layers),
  laenge: z.coerce.number().optional(),
  breite: z.coerce.number().optional(),
  zoom: z.coerce.number().optional(),
});

const zValidateSearch = z.union([
  zMapSearchBase.extend({
    id: z.string(),
    type: zFeatureType,
  }),
  zMapSearchBase.extend({
    id: z.undefined().optional(),
    type: z.undefined().optional(),
  }),
]);

export const Route = createFileRoute('/_public/_map')({
  validateSearch: zValidateSearch,
  search: {
    middlewares: [stripSearchParams(defaultSearchValues)],
  },
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: PublicIndexRoute,
});

function PublicIndexRoute() {
  const { data: user } = useOptionalUserQuery();
  const { isDesktop } = useScreenSize();

  return (
    <Root direction="column">
      <HeaderWrapper>{user || !isDesktop ? <Header /> : null}</HeaderWrapper>
      <MapProvider>
        <MapWrapper flex={1}>
          <PublicMap />

          <Outlet />
        </MapWrapper>
      </MapProvider>

      <FooterWrapper>
        <Footer />
      </FooterWrapper>
    </Root>
  );
}
